import {
  Archive,
  ArrowLeft,
  CaretDown,
  ChatsCircle,
  MagnifyingGlass,
  X,
} from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../chat.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { KuniriLogo } from '@/components/kuniri-logo'
import { EmptyState } from '@/features/discovery/components/empty-state'
import { PullToRefresh } from '@/features/discovery/components/pull-to-refresh'
import { useIsDesktop } from '@/features/discovery/hooks/use-is-desktop'

import {
  chatApi,
  type ActivityChatSummary,
  type Friend,
  type PrivateConversation,
} from '../api'
import { ChatConfirmDialog } from '../components/chat-confirm-dialog'
import { ChatNotice, type ChatNoticeState } from '../components/chat-notice'
import {
  ChatRefreshButton,
  ChatRefreshStatus,
} from '../components/chat-refresh'
import { ChatRow } from '../components/chat-row'
import { ChatSearchResults } from '../components/chat-search-results'
import {
  archivedUnreadTotal,
  chatErrorKey,
  splitChatList,
  type ChatListItem,
} from '../format'
import { useChatLists } from '../hooks/use-chat-lists'
import { useChatSearch } from '../hooks/use-chat-search'
import {
  activityDraftKey,
  conversationDraftKey,
  useChatUiStore,
} from '../state/chat-ui-store'

type PendingDelete =
  | { kind: 'activity'; chat: ActivityChatSummary }
  | { kind: 'private'; conversation: PrivateConversation }
  | null

/**
 * `/chats`: one list for activity chats and private conversations.
 * Pinned first, then latest message; archived activities live in the
 * "Attività passate" expansion. Route-local state only (D-042).
 */
export function ChatsScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const {
    activityChats,
    privateChats,
    phase,
    refreshing,
    error,
    refresh,
    setActivityChats,
    setPrivateChats,
  } = useChatLists()

  const query = useChatUiStore((state) => state.search)
  const setQuery = useChatUiStore((state) => state.setSearch)
  const archivedOpen = useChatUiStore((state) => state.archivedOpen)
  const setArchivedOpen = useChatUiStore((state) => state.setArchivedOpen)
  const search = useChatSearch(query)
  const [notice, setNotice] = useState<ChatNoticeState | null>(null)
  const [pendingPin, setPendingPin] = useState<Set<string>>(() => new Set())
  const [pendingDelete, setPendingDelete] = useState<Set<string>>(
    () => new Set(),
  )
  const [openingFriendId, setOpeningFriendId] = useState<string | null>(null)
  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<PendingDelete>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { active, archived } = splitChatList(activityChats, privateChats)
  const archivedUnread = archivedUnreadTotal(archived)
  const searching = query.trim().length > 0

  // Preserve the tab scroll position without re-rendering on every pixel.
  useEffect(() => {
    if (phase !== 'ready') return
    const stored = useChatUiStore.getState().listScrollY
    if (stored > 0) window.scrollTo({ top: stored })
    const onScroll = () =>
      useChatUiStore.getState().setListScrollY(window.scrollY)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [phase])

  useEffect(() => {
    if (!searchOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0)
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setSearchOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [searchOpen, setQuery])

  function setPending(
    setter: (updater: (current: Set<string>) => Set<string>) => void,
    key: string,
    pending: boolean,
  ) {
    setter((current) => {
      const next = new Set(current)
      if (pending) next.add(key)
      else next.delete(key)
      return next
    })
  }

  async function togglePin(item: ChatListItem) {
    const key = `${item.kind}:${item.id}`
    if (pendingPin.has(key)) return
    setPending(setPendingPin, key, true)

    if (item.kind === 'activity') {
      const nextPinned = !item.pinned
      setActivityChats((items) =>
        items.map((chat) =>
          chat.id === item.id ? { ...chat, pinned: nextPinned } : chat,
        ),
      )
      try {
        await chatApi.pinActivityChat(item.id, nextPinned)
        setActivityChats((items) =>
          items.map((chat) =>
            chat.id === item.id ? { ...chat, pinned: nextPinned } : chat,
          ),
        )
        setNotice({
          messageKey: nextPinned
            ? 'chat.notice.pinned'
            : 'chat.notice.unpinned',
          tone: 'success',
        })
      } catch (cause) {
        setActivityChats((items) =>
          items.map((chat) =>
            chat.id === item.id ? { ...chat, pinned: item.pinned } : chat,
          ),
        )
        setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
      } finally {
        setPending(setPendingPin, key, false)
      }
      return
    }

    const nextPinned = !item.pinned
    setPrivateChats((items) =>
      items.map((conversation) =>
        conversation.id === item.id
          ? { ...conversation, pinned: nextPinned }
          : conversation,
      ),
    )
    try {
      await chatApi.pinPrivateConversation(item.id, nextPinned)
      setPrivateChats((items) =>
        items.map((conversation) =>
          conversation.id === item.id
            ? { ...conversation, pinned: nextPinned }
            : conversation,
        ),
      )
      setNotice({
        messageKey: nextPinned ? 'chat.notice.pinned' : 'chat.notice.unpinned',
        tone: 'success',
      })
    } catch (cause) {
      setPrivateChats((items) =>
        items.map((conversation) =>
          conversation.id === item.id
            ? { ...conversation, pinned: item.pinned }
            : conversation,
        ),
      )
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setPending(setPendingPin, key, false)
    }
  }

  async function openFriend(friend: Friend) {
    if (openingFriendId) return
    setOpeningFriendId(friend.userId)
    try {
      const conversation = await chatApi.openPrivateConversation(friend.userId)
      navigate(`/messages/${conversation.id}`)
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause, { privateChat: true }), tone: 'error' })
    } finally {
      setOpeningFriendId(null)
    }
  }

  async function confirmDelete() {
    if (!pendingDeleteTarget) return
    const key =
      pendingDeleteTarget.kind === 'activity'
        ? `activity:${pendingDeleteTarget.chat.id}`
        : `private:${pendingDeleteTarget.conversation.id}`
    if (pendingDelete.has(key)) return
    setPending(setPendingDelete, key, true)
    try {
      if (pendingDeleteTarget.kind === 'activity') {
        await chatApi.hideActivityChat(pendingDeleteTarget.chat.id)
        useChatUiStore
          .getState()
          .clearDraft(activityDraftKey(pendingDeleteTarget.chat.id))
        setActivityChats((items) =>
          items.filter((chat) => chat.id !== pendingDeleteTarget.chat.id),
        )
        setNotice({ messageKey: 'chat.notice.activityHidden', tone: 'success' })
      } else {
        await chatApi.hidePrivateConversation(
          pendingDeleteTarget.conversation.id,
        )
        useChatUiStore
          .getState()
          .clearDraft(conversationDraftKey(pendingDeleteTarget.conversation.id))
        setPrivateChats((items) =>
          items.filter(
            (conversation) =>
              conversation.id !== pendingDeleteTarget.conversation.id,
          ),
        )
        setNotice({ messageKey: 'chat.notice.privateHidden', tone: 'success' })
      }
      setPendingDeleteTarget(null)
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setPending(setPendingDelete, key, false)
    }
  }

  function deleteMenuFor(item: ChatListItem) {
    if (item.kind === 'private') {
      return () =>
        setPendingDeleteTarget({ kind: 'private', conversation: item.conversation })
    }
    if (item.chat.archived) {
      return () =>
        setPendingDeleteTarget({ kind: 'activity', chat: item.chat })
    }
    return undefined
  }

  const deleteBusy = pendingDeleteTarget
    ? pendingDelete.has(
        pendingDeleteTarget.kind === 'activity'
          ? `activity:${pendingDeleteTarget.chat.id}`
          : `private:${pendingDeleteTarget.conversation.id}`,
      )
    : false

  function closeSearch() {
    setSearchOpen(false)
    setQuery('')
  }

  const searchField = (autoFocus: boolean) => (
    <div className="chat-search flex-1">
      <label className="sr-only" htmlFor="chat-search-input">
        {t('chat.search.label')}
      </label>
      <span className="chat-search__icon" aria-hidden="true">
        <MagnifyingGlass size={19} weight="bold" />
      </span>
      <input
        id="chat-search-input"
        type="search"
        className="chat-search__input"
        value={query}
        placeholder={t('chat.search.placeholder')}
        ref={autoFocus ? searchInputRef : undefined}
        onChange={(event) => setQuery(event.target.value)}
      />
      {query ? (
        <button
          type="button"
          className="chat-search__clear"
          onClick={() => setQuery('')}
          aria-label={t('chat.search.clear')}
        >
          <X size={18} weight="bold" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )

  return (
    <section aria-labelledby="chats-title" className="content-width pb-10">
      <header className="compact-sticky-header flex flex-wrap items-center gap-2 py-1">
        <Link
          to="/home"
          className="mr-auto inline-flex min-h-11 items-center"
          aria-label={t('common.appName')}
        >
          <KuniriLogo height={24} />
        </Link>
        {isDesktop ? (
          <ChatRefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </header>

      <h1
        id="chats-title"
        className="sr-only md:not-sr-only md:text-[clamp(2rem,4vw,3rem)]"
      >
        {t('screens.chats.title')}
      </h1>

      {isDesktop ? (
        <div className="chat-search mt-4">{searchField(false)}</div>
      ) : (
        <button
          type="button"
          className="btn-secondary mt-3 min-h-12 w-full justify-start bg-white px-4 text-base font-semibold"
          onClick={() => setSearchOpen(true)}
        >
          <MagnifyingGlass size={19} weight="bold" aria-hidden="true" />
          {t('chat.search.placeholder')}
        </button>
      )}

      {searchOpen && !isDesktop ? (
        <div
          className="fixed inset-0 z-50 flex min-h-dvh flex-col bg-paper px-4 pt-[calc(0.75rem+var(--safe-top))] pb-[calc(1rem+var(--safe-bottom))]"
          role="dialog"
          aria-modal="true"
          aria-label={t('chat.search.label')}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
              onClick={closeSearch}
              aria-label={t('common.close')}
            >
              <ArrowLeft size={22} weight="bold" aria-hidden="true" />
            </button>
            {searchField(true)}
          </div>
          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            <ChatSearchResults
              phase={search.phase}
              term={search.term || query.trim()}
              friends={search.friends}
              activities={search.activities}
              openingFriendId={openingFriendId}
              onOpenFriend={(friend) => void openFriend(friend)}
            />
          </div>
        </div>
      ) : null}

      <ChatNotice notice={notice} onDismiss={() => setNotice(null)} />
      {phase === 'ready' && error ? (
        <p className="chat-notice chat-notice--error" role="alert">
          {t(chatErrorKey(error, {privateChat: false}))}
        </p>
      ) : null}

      <PullToRefresh
        className="mt-4"
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <ChatRefreshStatus visible={refreshing} />

        {searching && isDesktop ? (
          <ChatSearchResults
            phase={search.phase}
            term={search.term || query.trim()}
            friends={search.friends}
            activities={search.activities}
            openingFriendId={openingFriendId}
            onOpenFriend={(friend) => void openFriend(friend)}
          />
        ) : phase === 'loading' ? (
          <LoadingState labelKey="chat.list.loading" />
        ) : phase === 'error' ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <>
            {active.length === 0 ? (
              <EmptyState
                compact
                icon={
                  <ChatsCircle size={28} weight="bold" aria-hidden="true" />
                }
                titleKey="chat.list.empty.title"
                bodyKey="chat.list.empty.body"
              />
            ) : (
              <ul className="chat-list">
                {active.map((item) => (
                  <ChatRow
                    key={`${item.kind}:${item.id}`}
                    item={item}
                    pinned={item.pinned}
                    unreadCount={
                      item.kind === 'activity'
                        ? item.chat.unreadCount
                        : item.conversation.unreadCount
                    }
                    onPin={() => void togglePin(item)}
                    onDelete={deleteMenuFor(item)}
                    busy={pendingPin.has(`${item.kind}:${item.id}`)}
                  />
                ))}
              </ul>
            )}

            <details
              className="chat-archived"
              open={archivedOpen}
              onToggle={(event) => setArchivedOpen(event.currentTarget.open)}
            >
              <summary className="chat-archived__summary">
                <Archive size={20} weight="bold" aria-hidden="true" />
                <span className="chat-archived__label">
                  {t('chat.list.archived', { count: archived.length })}
                </span>
                {archivedUnread > 0 ? (
                  <span className="chat-archived__unread">
                    {t('chat.list.unread', { count: archivedUnread })}
                  </span>
                ) : null}
                <CaretDown
                  size={18}
                  weight="bold"
                  aria-hidden="true"
                  className="chat-archived__caret"
                />
              </summary>
              <div className="chat-archived__body">
                {archived.length === 0 ? (
                  <p className="chat-archived__empty">
                    {t('chat.list.archivedEmpty')}
                  </p>
                ) : (
                  <ul className="chat-list">
                    {archived.map((chat) => (
                      <ChatRow
                        key={`activity:${chat.id}`}
                        item={{
                          kind: 'activity',
                          id: chat.id,
                          pinned: chat.pinned,
                          lastMessageAt: chat.lastMessageAt,
                          chat,
                        }}
                        pinned={chat.pinned}
                        unreadCount={chat.unreadCount}
                        onPin={() =>
                          void togglePin({
                            kind: 'activity',
                            id: chat.id,
                            pinned: chat.pinned,
                            lastMessageAt: chat.lastMessageAt,
                            chat,
                          })
                        }
                        onDelete={() =>
                          setPendingDeleteTarget({
                            kind: 'activity',
                            chat,
                          })
                        }
                        busy={pendingPin.has(`activity:${chat.id}`)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </details>
          </>
        )}
      </PullToRefresh>

      <ChatConfirmDialog
        open={pendingDeleteTarget !== null}
        titleKey={
          pendingDeleteTarget?.kind === 'private'
            ? 'chat.confirm.private.title'
            : 'chat.confirm.activity.title'
        }
        bodyKey={
          pendingDeleteTarget?.kind === 'private'
            ? 'chat.confirm.private.body'
            : 'chat.confirm.activity.body'
        }
        confirmLabelKey="chat.confirm.delete"
        busy={deleteBusy}
        onCancel={() => setPendingDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </section>
  )
}
